import { memo } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { PORTAL_PATH } from '../../../routes/paths';
import { Icon, IconType } from '../../../common/icon';
import { convertToKebabCase } from '../../../../constants/helpers/text';

interface StyleProps {
  isSelected: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  tabContainer: {
    color: ({ isSelected }: StyleProps) => (isSelected ? theme.palette.primary.main : theme.palette.common.black),
    borderBottomStyle: 'solid',
    borderBottomWidth: ({ isSelected }: StyleProps) => (isSelected ? '2px' : '1px'),
    borderBottomColor: ({ isSelected }: StyleProps) =>
      isSelected ? theme.palette.primary.main : theme.customPalette.silverSand,
    paddingBottom: ({ isSelected }: StyleProps) => (isSelected ? '12px' : '13px'),
    cursor: ({ isSelected }: StyleProps) => (isSelected ? 'default' : 'pointer'),
    boxSizing: 'border-box'
  },
  label: {
    display: 'flex',
    margin: theme.spacing(0, 2),
    alignItems: 'center'
  },
  text: {
    fontWeight: '400',
    fontSize: '18px',
    lineHeight: '24px',
    color: 'inherit'
  },
  icon: {
    fontSize: theme.elements.icon.size.m,
    color: 'inherit',
    marginRight: theme.spacing(0.5)
  },
  routerLinkWrapper: {
    textDecoration: 'none'
  }
}));

export interface Props {
  icon: IconType;
  title: string;
  path: PORTAL_PATH;
}

const Tab = ({ icon, title, path }: Props) => {
  const { pathname } = useLocation();
  const classes = useStyles({ isSelected: path === pathname });
  const theme = useTheme();

  const isSelected = path === pathname;
  return (
    <RouterLink to={path} className={classes.routerLinkWrapper}>
      <Box className={classes.tabContainer} data-testid={`${convertToKebabCase(title)}-tab-label`}>
        <Box className={classes.label}>
          <Icon icon={icon} className={classes.icon} />
          <Typography
            color={isSelected ? theme.palette.primary.main : theme.customPalette.silverSand}
            className={classes.text}
          >
            {title}
          </Typography>
        </Box>
      </Box>
    </RouterLink>
  );
};

export default memo(Tab);
